import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppTable from "../../../../../common/AppTable/AppTable";
import RenderHtml from "../../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { getAllProcedures } from "../../../../../redux/partSlice";
import { TableActionGroup } from "../../../../../common/Buttons";
import OpenTasksPopup from "../../../../../pages/Tasks/Popups/OpenTasksPopup";

/**
 * SelectRevisionTable Component
 * Reusable table component for selecting document revisions during copy operations
 * Uses AppTable for consistent UI and functionality
 *
 * @param {Object} props
 * @param {string} props.sourcePartNumber - The part number to exclude from results (source document)
 * @param {Function} props.onRevisionSelect - Callback when revision is selected
 * @param {string} props.fromRevision - The revision to exclude from results
 * @param {string} props.procedureRevisionId - The revision ID to hide from results
 * @param {Object} props.filterOptions - Additional filtering options
 */
const SelectRevisionTable = ({
  sourcePartNumber,
  onRevisionSelect,
  fromRevision,
  procedureRevisionId,
  filterOptions = {},
  isPendingTaskCheckRequired = true,
}) => {
  const [groupedRows, setGroupedRows] = useState([]);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [showTaskPopup, setShowTaskPopup] = useState(false);
  const [selectedRevisionForCheck, setSelectedRevisionForCheck] = useState(null);

  const dispatch = useDispatch();
  const { procedures, loading, pagination } = useSelector(
    (state) => state.parts
  );
  const tableContainerRef = useRef();

  // Define columns for AppTable
  const columns = [
    {
      headName: "Item Code",
      field: "partNumber",
      sort: true,
      filter: true,
      width: "15%",
    },
    {
      headName: "Type",
      field: "type",
      sort: true,
      filter: true,
      width: "15%",
    },
    {
      headName: "Description",
      field: "description",
      sort: false,
      filter: true,
      width: "45%",
    },
    {
      headName: "Status",
      field: "status",
      sort: true,
      filter: true,
      width: "10%",
    },
    {
      headName: "Rev",
      field: "rev",
      sort: false,
      filter: false,
      width: "5%",
    },
    // {
    //   headName: "Action",
    //   field: "action",
    //   sort: false,
    //   filter: false,
    //   width: "5%",
    //   textCenter: true,
    //   isAction: true,
    // },
    { headName: "Action", field: "action", textCenter: true, isAction: true },
  ];

  // Reset offset to 0 when search query, search type, or sort changes
  useEffect(() => {
    setOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchType, sortBy, sortOrder]);

  // Fetch procedures when limit, offset, searchQuery, searchType, or sort changes
  useEffect(() => {
    // Map frontend field names to backend API field names
    // Frontend uses "partNumber" but backend expects "part_number"
    // Frontend uses "type" but backend expects "type_name"
    // Frontend uses "description" but backend expects "part_description"
    // Frontend uses "status" and backend expects "status"
    const mapFieldNames = (fieldNames) => {
      if (!fieldNames) return "";

      const fieldMapping = {
        partNumber: "part_number",
        type: "type_name",
        description: "description",
        status: "status",
      };

      // Map all fields including status
      const mappedFields = fieldNames
        .split(",")
        .map((field) => field.trim())
        .map((field) => fieldMapping[field] || field)
        .filter((field) => field) // Remove empty strings
        .join(",");

      return mappedFields;
    };

    const mappedSearchType = mapFieldNames(searchType);

    // Map sort field name to backend field name
    // AppTable converts "type" to "name" for sorting, so we need to map both
    const mapSortField = (field) => {
      if (!field) return "";

      const sortFieldMapping = {
        partNumber: "part_number",
        name: "type_name", // AppTable sends "name" when sorting by "type" column
        type: "type_name", // Fallback in case it sends "type" directly
        status: "status",
      };

      return sortFieldMapping[field] || field;
    };

    const mappedSortBy = mapSortField(sortBy);

    dispatch(
      getAllProcedures({
        limit,
        offset,
        search: searchQuery,
        search_type: mappedSearchType,
        sort_by: mappedSortBy,
        sort_order: sortOrder,
        is_hide_current: true,
        procedure_revision_id: procedureRevisionId || "",
      })
    );
  }, [
    dispatch,
    limit,
    offset,
    searchQuery,
    searchType,
    sortBy,
    sortOrder,
    procedureRevisionId,
  ]);

  // Group procedures by part number and status
  useEffect(() => {
    const groupMap = new Map();

    procedures.forEach((proc) => {
      const partNumber = proc.part_number;
      const status = proc.status_changes?.[0]?.status_changed_to || "Unknown";
      const key = `${partNumber}__${status}`;

      // Skip if status is not allowed or if this is the source Item Code
      if (status !== "Not Submitted" && status !== "Change Requested") return;
      // if (partNumber === sourcePartNumber && proc.rev === fromRevision) return;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          partNumber,
          type: proc.type_name,
          description: proc.part_description,
          status,
          partId: proc.part_ref,
          part_url: proc.part_url,
          revisions: [],
        });
      }

      groupMap.get(key).revisions.push({
        id: proc.procedure_revision_id,
        label: proc.rev,
        created_at: proc.created_at,
        full: proc,
      });
    });

    // Sort each group's revisions by created_at (latest first)
    const rows = Array.from(groupMap.values()).map((group) => {
      group.revisions.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      group.latestRevision = group.revisions[0];
      return group;
    });

    setGroupedRows(rows);
  }, [procedures, sourcePartNumber]);

  // Use groupedRows directly since filtering is done server-side
  const displayRows = groupedRows;

  return (
    <>
      <AppTable
        ref={tableContainerRef}
        columnsData={columns}
        showSearchInput={true}
        searchInputPosition="left"
        paginate={true}
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination,
          maxVsbleButtons: 7,
          justifyContent: "center",
          paginationPosition: {
            position: "right",
          },
          paginationShadow: false,
        }}
        onFilterSortHandler={(query) => {
          // Extract search query, search_type, sort_by, and sort_order from the query string
          // AppTable passes query as URL string format: "limit=10&offset=0&search_type=type,status&search=...&sort_by=name&sort_order=ASC"
          let search = "";
          let search_type = "";
          let sort_by = "";
          let sort_order = "";

          if (typeof query === "string" && query.trim()) {
            try {
              // URLSearchParams works with query strings (with or without ? prefix)
              // Ensure query starts with ? for proper parsing
              const queryStr = query.startsWith("?") ? query : `?${query}`;
              const params = new URLSearchParams(queryStr);

              // Get search parameter (returns null if not present, empty string if present but empty)
              const searchParam = params.get("search");
              const typeParam = params.get("search_type");
              const sortByParam = params.get("sort_by");
              const sortOrderParam = params.get("sort_order");

              if (searchParam !== null) {
                search = searchParam;
              }
              if (typeParam !== null) {
                search_type = typeParam;
              }
              if (sortByParam !== null) {
                sort_by = sortByParam;
              }
              if (sortOrderParam !== null) {
                sort_order = sortOrderParam;
              }
            } catch (e) {
              // Fallback: Use regex to extract parameters if URLSearchParams fails
              const searchMatch = query.match(/(?:^|[&?])search=([^&]*)/);
              const typeMatch = query.match(/(?:^|[&?])search_type=([^&]*)/);
              const sortByMatch = query.match(/(?:^|[&?])sort_by=([^&]*)/);
              const sortOrderMatch = query.match(
                /(?:^|[&?])sort_order=([^&]*)/
              );

              if (searchMatch && searchMatch[1] !== undefined) {
                try {
                  search = decodeURIComponent(searchMatch[1]);
                } catch (decodeError) {
                  search = searchMatch[1];
                }
              }

              if (typeMatch && typeMatch[1] !== undefined) {
                try {
                  search_type = decodeURIComponent(typeMatch[1]);
                } catch (decodeError) {
                  search_type = typeMatch[1];
                }
              }

              if (sortByMatch && sortByMatch[1] !== undefined) {
                try {
                  sort_by = decodeURIComponent(sortByMatch[1]);
                } catch (decodeError) {
                  sort_by = sortByMatch[1];
                }
              }

              if (sortOrderMatch && sortOrderMatch[1] !== undefined) {
                try {
                  sort_order = decodeURIComponent(sortOrderMatch[1]);
                } catch (decodeError) {
                  sort_order = sortOrderMatch[1];
                }
              }
            }
          }

          setSearchQuery(search);
          setSearchType(search_type);
          setSortBy(sort_by);
          setSortOrder(sort_order);
        }}
        extrapadding="0px"
        usingInPopup={false}
      >
        {/* Render table rows - each revision gets its own row */}
        {displayRows.flatMap((group, groupIndex) =>
          group.revisions.map((revision, revIndex) => (
            <tr key={`${groupIndex}-${revIndex}`}>
              <td>
                {group.part_url ? (
                  <a
                    href={group.part_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "black", textDecoration: "underline" }}
                  >
                    {group.partNumber}
                  </a>
                ) : (
                  group.partNumber
                )}
              </td>
              <td>{group.type}</td>
              <td>
                <RenderHtml content={group.description} />
              </td>
              <td>{group.status}</td>
              <td style={{ textAlign: "center" }}>
                <span style={{ fontSize: "14px", color: "#444" }}>
                  {revision.label}
                </span>
              </td>
              <td>
                <TableActionGroup
                  mode="view"
                  showEdit={false}
                  showDelete={false}
                  showSelect={true}
                  onSelect={() => {
                    if (isPendingTaskCheckRequired) {
                      setSelectedRevisionForCheck({ group, revision });
                      setShowTaskPopup(true);
                    } else {
                      onRevisionSelect({ group, revision });
                    }
                                      
                    }
                  }
                />
              </td>
            </tr>
          ))
        )}

        {/* Show empty state when no results */}
        {displayRows.length === 0 && !loading && (
          <tr>
            <td
              colSpan={columns.length}
              style={{ textAlign: "center", padding: "40px" }}
            >
              <div style={{ color: "#666", fontSize: "16px" }}>
                No documents found
              </div>
            </td>
          </tr>
        )}
      </AppTable>
      {showTaskPopup && selectedRevisionForCheck && (
        <OpenTasksPopup
          closeModal={() => {
            setShowTaskPopup(false);
            setSelectedRevisionForCheck(null);
          }}
          procedure_ltree_ref={
            selectedRevisionForCheck?.revision?.full?.tree_node_details
              ?.ltree_path
          }
          onContinue={() => {
            onRevisionSelect(selectedRevisionForCheck);
            setShowTaskPopup(false);
            setSelectedRevisionForCheck(null);
          }}
          isContinueDisabled={true}
          title="Pending Tasks"
        />
      )}
    </>
  );
};

export default SelectRevisionTable;
